// ПИН клавиатура като в банково приложение: 4 точки + големи кръгли
// бутони, без системната клавиатура. Родителят държи стойността; когато
// стигне 4 цифри, проверява и при грешка вика shake() + подава value=''.

import React, { useEffect, useRef } from 'react';
import { Animated, Pressable, Text, View } from 'react-native';
import { COLORS } from './ui';

export function usePinShake(): { offset: Animated.Value; shake: () => void } {
  const offset = useRef(new Animated.Value(0)).current;
  const shake = () => {
    Animated.sequence(
      [12, -12, 8, -8, 4, 0].map((to) =>
        Animated.timing(offset, { toValue: to, duration: 55, useNativeDriver: true })
      )
    ).start();
  };
  return { offset, shake };
}

export function PinDots({ value, offset }: { value: string; offset?: Animated.Value }) {
  return (
    <Animated.View
      style={{
        flexDirection: 'row',
        gap: 18,
        justifyContent: 'center',
        marginVertical: 22,
        transform: offset ? [{ translateX: offset }] : [],
      }}
    >
      {[0, 1, 2, 3].map((i) => {
        const filled = i < value.length;
        return (
          <View
            key={i}
            style={{
              width: 16,
              height: 16,
              borderRadius: 8,
              backgroundColor: filled ? COLORS.primary : 'transparent',
              borderWidth: 1.5,
              borderColor: filled ? COLORS.primary : COLORS.muted,
            }}
          />
        );
      })}
    </Animated.View>
  );
}

const KEYS: Array<string> = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

export function PinPad({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  // Физическата клавиатура също работи (уеб/таблет с клавиатура)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      if (/^\d$/.test(e.key) && value.length < 4) onChange(value + e.key);
      if (e.key === 'Backspace') onChange(value.slice(0, -1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [value, disabled, onChange]);

  return (
    <View style={{ width: 272, alignSelf: 'center' }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
        {KEYS.map((k, i) =>
          k === '' ? (
            <View key={i} style={{ width: 72, height: 72 }} />
          ) : (
            <Pressable
              key={i}
              disabled={disabled}
              onPress={() => {
                if (k === '⌫') onChange(value.slice(0, -1));
                else if (value.length < 4) onChange(value + k);
              }}
              style={({ pressed }) => ({
                width: 72,
                height: 72,
                borderRadius: 36,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: pressed ? COLORS.primary : COLORS.card,
                borderWidth: 1,
                borderColor: pressed ? COLORS.primary : COLORS.border,
              })}
            >
              {({ pressed }) => (
                <Text
                  style={{
                    fontSize: k === '⌫' ? 22 : 26,
                    fontWeight: '600',
                    color: pressed ? '#fff' : COLORS.text,
                  }}
                >
                  {k}
                </Text>
              )}
            </Pressable>
          )
        )}
      </View>
    </View>
  );
}
